import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { SalesStatistics } from "interface/sales";
import { GrideDateFormatter } from "ui/Date";
import { FieldFull, FieldSplit } from "ui/CardField";
import { formatDate } from "utils/formatters";

interface StoreStatisticsMobileListProps {
	items: SalesStatistics[];
	loading: boolean;
}

const formatWeight = (value?: number | null) => {
	if (value === undefined || value === null) {
		return "-";
	}

	const asNumber = Number(value || 0);

	return `${asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
	})} kg`;
};

const formatNumber = (value?: number | null, fractionDigits = 0) => {
	const asNumber = Number(value || 0);
	return asNumber.toLocaleString("no-NO", {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	});
};

const formatVessel = (item: SalesStatistics) => {
	const registration = item.vesselRegistrationNumber?.trim();
	const vesselName = item.vesselDescription?.trim();

	if (registration && vesselName) {
		return `${registration} - ${vesselName}`;
	}

	return registration || vesselName || "-";
};

const formatCatchArea = (item: SalesStatistics) => {
	const catchArea = item.catchAreaDescription || item.catchAreaCode || "-";
	const zone = item.catchAreaZone ? ` / ${item.catchAreaZone}` : "";
	return `${catchArea}${zone}`;
};

const StoreStatisticsMobileList: FC<StoreStatisticsMobileListProps> = ({
	items,
	loading,
}) => {
	const { t } = useTranslation();
	const empty = !loading && items.length === 0;

	return (
		<div className="store-statistics-list-mobile">
			{empty && (
				<div className="store-statistics-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, itemIndex) => {
				const itemKey = `${item.landingNoteNumber || "nolanding"}-${item.lineNumber || itemIndex}-${itemIndex}`;

				return (
					<div key={itemKey} className="store-statistics-list-card">
						<div className="store-statistics-list-card-header">
							<div className="store-statistics-list-card-vessel">
								{item.productDescription || "-"}
							</div>
						</div>

						<div className="store-statistics-list-card-body">
							<FieldFull
								label={t("Vessel")}
								value={formatVessel(item)}
							/>
							<FieldSplit
								leftLabel={t("Terminal")}
								leftValue={item.terminalDescription || "-"}
								rightLabel={t("DeliveryDate")}
								rightValue={formatDate(item.deliveryDateTime)}
							/>

							<FieldSplit
								leftLabel={t("Species")}
								leftValue={item.specieDescription || "-"}
								rightLabel={t("PreservationType")}
								rightValue={item.productPreservationType || "-"}
							/>
							<FieldFull
								label={t("FishingGear")}
								value={item.fishingGearDescription || "-"}
							/>
							<FieldFull
								label={t("CatchArea")}
								value={formatCatchArea(item)}
							/>


						</div>

						<div className="store-statistics-list-card-footer">
							<FieldSplit
								leftLabel={t("Weight")}
								leftValue={formatWeight(item.grossWeight)}
								rightLabel={t("RoundWeight")}
								rightValue={formatWeight(item.roundWeight)}
							/>
							<FieldSplit
								leftLabel={t("Quantity")}
								leftValue={formatNumber(item.quantity)}
								rightLabel={t("NumberOfUnits")}
								rightValue={formatNumber(item.numberOfUnits)}
							/>
						</div>
					</div>
				);
			})}

			{loading && (
				<div className="store-statistics-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default StoreStatisticsMobileList;